import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Save, X, Loader2, Globe, Plus } from 'lucide-react';
import { pois as poisApi, poiCategories as poiCategoriesApi } from '@/lib/trackizy';
import { poisActions } from '@/store';
import useScopedDispatch from '@/hooks/useScopedDispatch';
import { selectPOICategories } from '@/store/selectors';
import { POI_ICONS } from './poiUtils';

export default function POIForm({ poi, onSaved, onCancel }) {
  const { t } = useTranslation();
  const beginScoped = useScopedDispatch();
  const categories = useSelector(selectPOICategories);
  const orgId = useSelector((state) => state.org?.id);
  const isAdmin = useSelector((state) => state.session.user?.administrator);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [name, setName] = useState(poi?.name || '');
  const [description, setDescription] = useState(poi?.description || '');
  const [lat, setLat] = useState(poi?.latitude != null ? String(poi.latitude) : '');
  const [lng, setLng] = useState(poi?.longitude != null ? String(poi.longitude) : '');
  const [radius, setRadius] = useState(poi?.radius_meters ?? 100);
  const [categoryId, setCategoryId] = useState(poi?.category_id || '');
  const [color, setColor] = useState(poi?.color || '');
  const [icon, setIcon] = useState(poi?.icon || '');
  const [dwellMinutes, setDwellMinutes] = useState(poi?.dwell_minutes || '');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);

  // Is this a global POI (organization_id = null)?
  const isGlobal = poi && poi.organization_id === null;
  const isReadOnly = isGlobal && !isAdmin;

  // Allow external coordinate update (from map click)
  useEffect(() => {
    const handler = (e) => {
      setLat(String(e.detail.lat));
      setLng(String(e.detail.lng));
    };
    window.addEventListener('poi-map-click', handler);
    return () => window.removeEventListener('poi-map-click', handler);
  }, []);

  const selectedCategory = categories.find((c) => c.id === categoryId);
  const effectiveColor = color || selectedCategory?.color || '#3b82f6';

  const handleSave = async () => {
    if (!name.trim() || !lat || !lng || isNaN(lat) || isNaN(lng)) return;

    setSaving(true);
    setError(null);

    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      latitude: parseFloat(lat),
      longitude: parseFloat(lng),
      radius_meters: Number(radius),
      category_id: categoryId || null,
      color: color || null,
      icon: icon || selectedCategory?.icon || 'map-pin',
      dwell_minutes: dwellMinutes ? Number(dwellMinutes) : null,
      organization_id: orgId || null,
    };

    const scoped = beginScoped();
    try {
      let data;
      if (poi?.id) {
        // Edit existing POI
        data = await poisApi.update(poi.id, payload);
      } else {
        // Create new POI
        data = await poisApi.create(payload);
      }

      scoped(poisActions.update(data));
      onSaved?.(data);
    } catch (err) {
      console.error('POI save failed:', err);
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const canSave = name.trim() && lat && lng && !isNaN(lat) && !isNaN(lng) && !isReadOnly;

  if (isReadOnly) {
    return (
      <div className="p-3 space-y-2 text-xs">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-500">
          <Globe size={13} />
          <span>{t('pois.globalReadOnly', 'Global POI — read only')}</span>
        </div>
        <button
          onClick={onCancel}
          className="h-8 px-3 flex items-center gap-1 rounded-lg text-[11px] font-semibold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all"
        >
          <X size={12} /> {t('pois.cancel')}
        </button>
      </div>
    );
  }

  // Group categories into global (org=null) and custom (org=orgId)
  const globalCats = categories.filter((c) => c.organization_id === null);
  const customCats = categories.filter((c) => c.organization_id !== null);

  return (
    <div className="p-3 space-y-3 text-xs">
      {/* Global POI banner */}
      {isGlobal && isAdmin && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[11px]">
          <Globe size={12} />
          <span>{t('pois.globalPoi', 'Global POI (visible to all orgs)')}</span>
        </div>
      )}

      {/* Name */}
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('pois.poiName')}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 dark:text-slate-200"
        autoFocus
      />

      {/* Category — sentinel '__new__' option flips to an inline create input. */}
      <div>
        <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('pois.category')}</label>
        {creatingCategory ? (
          <div className="mt-1 flex items-center gap-1.5">
            <input
              autoFocus
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!newCategoryName.trim() || savingCategory) return;
                  setSavingCategory(true);
                  const scoped = beginScoped();
                  try {
                    const created = await poiCategoriesApi.create({
                      name: newCategoryName.trim(),
                      color: '#3b82f6',
                      icon: 'map-pin',
                      dwell_minutes: 5,
                    });
                    scoped(poisActions.updateCategory(created));
                    setCategoryId(created.id);
                    setCreatingCategory(false);
                    setNewCategoryName('');
                  } catch (err) {
                    setError(err?.message || 'Failed to create category');
                  } finally {
                    setSavingCategory(false);
                  }
                } else if (e.key === 'Escape') {
                  setCreatingCategory(false);
                  setNewCategoryName('');
                }
              }}
              placeholder={t('pois.newCategoryPlaceholder', 'Category name…')}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-200"
            />
            <button
              type="button"
              disabled={!newCategoryName.trim() || savingCategory}
              onClick={async () => {
                if (!newCategoryName.trim()) return;
                setSavingCategory(true);
                const scoped = beginScoped();
                try {
                  const created = await poiCategoriesApi.create({
                    name: newCategoryName.trim(),
                    color: '#3b82f6',
                    icon: 'map-pin',
                    dwell_minutes: 5,
                  });
                  scoped(poisActions.updateCategory(created));
                  setCategoryId(created.id);
                  setCreatingCategory(false);
                  setNewCategoryName('');
                } catch (err) {
                  setError(err?.message || 'Failed to create category');
                } finally {
                  setSavingCategory(false);
                }
              }}
              className="h-9 px-2.5 text-[11px] font-semibold rounded-lg bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-40"
            >
              {savingCategory ? <Loader2 size={12} className="animate-spin" /> : t('common.add', 'Add')}
            </button>
            <button
              type="button"
              onClick={() => { setCreatingCategory(false); setNewCategoryName(''); }}
              className="h-9 px-2 text-[11px] font-semibold rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <select
            value={categoryId}
            onChange={(e) => {
              if (e.target.value === '__new__') {
                setCreatingCategory(true);
              } else {
                setCategoryId(e.target.value);
              }
            }}
            className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-200"
          >
            <option value="">—</option>
            {globalCats.length > 0 && (
              <optgroup label={t('pois.globalCategories', 'Global')}>
                {globalCats.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </optgroup>
            )}
            {customCats.length > 0 && (
              <optgroup label={t('pois.customCategories', 'Custom')}>
                {customCats.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </optgroup>
            )}
            <option value="__new__">+ {t('pois.newCategory', 'New category…')}</option>
          </select>
        )}
      </div>

      {/* Coordinates */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('pois.latitude')}</label>
          <input
            type="number"
            step="any"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-200"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('pois.longitude')}</label>
          <input
            type="number"
            step="any"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-200"
          />
        </div>
      </div>

      {/* Radius */}
      <div>
        <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('pois.radius')}</label>
        <input
          type="number"
          min="10"
          max="5000"
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-200"
        />
      </div>

      {/* Description */}
      <div>
        <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('pois.description')}</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('pois.descriptionPlaceholder')}
          rows={2}
          className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-200 resize-none"
        />
      </div>

      {/* Visit threshold override */}
      <div>
        <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('pois.visitThreshold')}</label>
        <input
          type="number"
          min="1"
          value={dwellMinutes}
          onChange={(e) => setDwellMinutes(e.target.value)}
          placeholder={selectedCategory ? `${selectedCategory.dwell_minutes} (${t('pois.category')})` : '5'}
          className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-200"
        />
      </div>

      {/* Icon override */}
      <div>
        <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('pois.icon')}</label>
        <select
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-200"
        >
          <option value="">{selectedCategory ? `${selectedCategory.icon} (category default)` : '—'}</option>
          {Object.keys(POI_ICONS).map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </div>

      {/* Color */}
      <div>
        <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('pois.color')}</label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="color"
            value={effectiveColor}
            onChange={(e) => setColor(e.target.value)}
            className="w-8 h-8 rounded-lg border border-slate-200 cursor-pointer"
          />
          <span className="text-slate-400">{effectiveColor}</span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <p className="text-[11px] text-red-500 font-medium">{error}</p>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave || saving}
          className="flex-1 h-8 flex items-center justify-center gap-1.5 rounded-lg text-[11px] font-semibold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 transition-all"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {t('pois.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-8 px-3 flex items-center gap-1 rounded-lg text-[11px] font-semibold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all"
        >
          <X size={12} /> {t('pois.cancel')}
        </button>
      </div>
    </div>
  );
}
